import React, { useState } from 'react';
import { Bouton, Etiquette } from './ui';

// Le brief se corrige à la main, section par section, dans les deux langues.
// Les listes s'éditent une ligne par élément ; les pièces et les étapes, bloc
// par bloc. On enregistre la langue affichée ; « Retraduire » refait le roumain
// depuis le français corrigé.

const lignes = (arr) => (arr || []).join('\n');
const deLignes = (s) => String(s || '').split('\n').map(x => x.trim()).filter(Boolean);

export default function BriefEditeur({ brief, onEnregistrer, onRetraduire, onFermer, occupe }) {
  const [langue, setLangue] = useState('fr');
  const [fr, setFr] = useState(brief.fr || {});
  const [ro, setRo] = useState(brief.ro || {});
  const b = langue === 'fr' ? fr : ro;
  const set = langue === 'fr' ? setFr : setRo;
  const maj = (champ, valeur) => set(x => ({ ...x, [champ]: valeur }));
  const majPiece = (i, champ, valeur) => set(x => ({ ...x, pieces: x.pieces.map((p, j) => (j === i ? { ...p, [champ]: valeur } : p)) }));
  const majEtape = (i, champ, valeur) => set(x => ({ ...x, etapes: x.etapes.map((e, j) => (j === i ? { ...e, [champ]: valeur } : e)) }));

  const Zone = ({ label, valeur, onChange, rows = 3, mono = false }) => (
    <label className="block">
      <Etiquette className="mb-1">{label}</Etiquette>
      <textarea value={valeur || ''} onChange={e => onChange(e.target.value)} rows={rows} className={`champ w-full ${mono ? 'font-mono text-xs' : ''}`} />
    </label>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center sm:p-4" onClick={onFermer}>
      <div className="bg-white w-full sm:max-w-4xl sm:rounded-2xl rounded-t-2xl shadow-2xl max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 p-4 border-b border-slate-100 shrink-0">
          <h2 className="text-base font-bold text-slate-900">Modifier le brief</h2>
          <div className="flex items-center gap-2">
            <div className="flex rounded-lg border border-slate-200 overflow-hidden text-xs font-semibold">
              {['fr', 'ro'].map(l => (
                <button key={l} onClick={() => setLangue(l)} className={`px-3 py-1.5 ${langue === l ? 'bg-slate-900 text-white' : 'bg-white text-slate-600'}`}>
                  {l === 'fr' ? 'Français' : 'Română'}
                </button>
              ))}
            </div>
            <button onClick={onFermer} className="p-1.5 text-slate-400 hover:text-slate-600" aria-label="Fermer">✕</button>
          </div>
        </div>

        <div className="overflow-y-auto p-4 space-y-4">
          {langue === 'ro' && !brief.ro && <p className="text-sm text-slate-500">Pas encore de version roumaine — enregistrez le français puis « Retraduire ».</p>}
          <Zone label="Titre" valeur={b.titre} onChange={v => maj('titre', v)} rows={1} />
          <Zone label="En bref" valeur={b.resume} onChange={v => maj('resume', v)} />
          <Zone label="Accès et contact" valeur={b.acces} onChange={v => maj('acces', v)} rows={2} />
          <div className="grid sm:grid-cols-2 gap-4">
            <Zone label="Consignes (une par ligne)" valeur={lignes(b.consignes)} onChange={v => maj('consignes', deLignes(v))} rows={6} />
            <Zone label="Sécurité (une par ligne)" valeur={lignes(b.securite)} onChange={v => maj('securite', deLignes(v))} rows={6} />
          </div>

          <div>
            <Etiquette className="mb-2">Étapes</Etiquette>
            <div className="space-y-2">
              {(b.etapes || []).map((e, i) => (
                <div key={i} className="grid sm:grid-cols-[3rem_1fr_1fr_6rem] gap-2 items-start">
                  <input value={e.ordre ?? i + 1} onChange={ev => majEtape(i, 'ordre', Number(ev.target.value) || i + 1)} className="champ w-full text-center" />
                  <input value={e.intitule || ''} onChange={ev => majEtape(i, 'intitule', ev.target.value)} className="champ w-full" placeholder="Intitulé" />
                  <textarea value={e.contenu || ''} onChange={ev => majEtape(i, 'contenu', ev.target.value)} rows={2} className="champ w-full" placeholder="Contenu" />
                  <input value={e.duree || ''} onChange={ev => majEtape(i, 'duree', ev.target.value)} className="champ w-full" placeholder="durée" />
                </div>
              ))}
              <Bouton taille="petit" onClick={() => maj('etapes', [...(b.etapes || []), { ordre: (b.etapes || []).length + 1, intitule: '', contenu: '', pieces: [], duree: '' }])}>+ Étape</Bouton>
            </div>
          </div>

          <div>
            <Etiquette className="mb-2">Pièce par pièce</Etiquette>
            <div className="space-y-3">
              {(b.pieces || []).map((p, i) => (
                <div key={i} className="rounded-xl border border-slate-200 p-3 space-y-2">
                  <input value={p.nom || ''} onChange={ev => majPiece(i, 'nom', ev.target.value)} className="champ w-full font-semibold" placeholder="Nom de la pièce" />
                  <textarea value={p.etat || ''} onChange={ev => majPiece(i, 'etat', ev.target.value)} rows={2} className="champ w-full" placeholder="État constaté" />
                  <Zone label="Travaux (une ligne par travail : intitulé — détails — quantité)" mono
                        valeur={(p.travaux || []).map(w => [w.intitule, w.details, w.quantite].filter(Boolean).join(' — ')).join('\n')}
                        onChange={v => majPiece(i, 'travaux', deLignes(v).map(l => { const [intitule, details = '', quantite = ''] = l.split(' — '); return { intitule, details, quantite }; }))}
                        rows={Math.max(3, (p.travaux || []).length + 1)} />
                  <Zone label="Attention (une par ligne)" valeur={lignes(p.attention)} onChange={v => majPiece(i, 'attention', deLignes(v))} rows={2} />
                </div>
              ))}
            </div>
          </div>

          <Zone label="Fournitures (une par ligne : désignation — quantité — unité)" mono
                valeur={(b.fournitures || []).map(f => [f.designation, f.quantite, f.unite].filter(Boolean).join(' — ')).join('\n')}
                onChange={v => maj('fournitures', deLignes(v).map(l => { const [designation, quantite = '', unite = ''] = l.split(' — '); return { designation, quantite, unite }; }))}
                rows={5} />
          <Zone label="À confirmer avant d'exécuter (une par ligne)" valeur={lignes(b.points_ouverts)} onChange={v => maj('points_ouverts', deLignes(v))} rows={3} />
        </div>

        <div className="flex flex-wrap items-center gap-2 p-4 border-t border-slate-100 shrink-0">
          <Bouton variante="primaire" onClick={() => onEnregistrer({ fr, ro: brief.ro ? ro : undefined })} disabled={occupe}>Enregistrer</Bouton>
          <Bouton onClick={() => onRetraduire({ fr })} disabled={occupe}>Enregistrer et retraduire en roumain</Bouton>
          <span className="text-xs text-slate-400 ml-auto">Les modifications du roumain sont perdues si vous retraduisez.</span>
        </div>
      </div>
    </div>
  );
}
